import React, { useMemo } from 'react';
import { Eye, CheckCircle2, AlertTriangle, AlertCircle, Info } from 'lucide-react';
import { Card } from '../../ui/Card';
import { Badge } from '../../ui/Badge';
import { useQualityWorkspaceState } from './useQualityWorkspaceState';
export function ValidationSuite() {
  const {
    filteredRecords
  } = useQualityWorkspaceState();
  const rows = useMemo(() => filteredRecords.map(record => ({
    ...record,
    issues: record.issues.filter(i => i.category === 'visuals')
  })).filter(record => record.issues.length > 0), [filteredRecords]);
  const summary = useMemo(() => {
    const allIssues = rows.flatMap(r => r.issues);
    return {
      questions: filteredRecords.length,
      affected: rows.length,
      critical: allIssues.filter(i => i.severity === 'critical').length,
      warning: allIssues.filter(i => i.severity === 'warning').length,
      info: allIssues.filter(i => i.severity === 'info').length
    };
  }, [rows, filteredRecords]);
  return <div className="space-y-4" data-id="element-2900">
      <Card className="p-5" data-id="element-2901">
        <div className="flex items-center justify-between mb-4" data-id="element-2902">
          <div className="flex items-center gap-2" data-id="element-2903">
            <div className="p-2 bg-indigo-50 rounded-lg" data-id="element-2904">
              <Eye className="w-5 h-5 text-indigo-600" data-id="element-2905" />
            </div>
            <div data-id="element-2906">
              <h3 className="text-lg font-bold text-slate-900" data-id="element-2907">
                Visual Governance
              </h3>
              <p className="text-sm text-slate-500" data-id="element-2908">
                {summary.affected} of {summary.questions} questions have visual
                issues
              </p>
            </div>
          </div>
          <div className="flex gap-2" data-id="element-2909">
            {summary.critical > 0 && <Badge variant="danger" size="sm" data-id="element-2910">
                {summary.critical} critical
              </Badge>}
            {summary.warning > 0 && <Badge variant="warning" size="sm" data-id="element-2911">
                {summary.warning} warnings
              </Badge>}
            {summary.affected === 0 && <Badge variant="success" size="sm" data-id="element-2912">
                All passing
              </Badge>}
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-id="element-2913">
          <SummaryBox label="Filtered Questions" value={summary.questions} color="slate" data-id="element-2914" />
          <SummaryBox label="Affected" value={summary.affected} color={summary.affected > 0 ? 'amber' : 'emerald'} data-id="element-2915" />
          <SummaryBox label="Critical" value={summary.critical} color={summary.critical > 0 ? 'rose' : 'emerald'} data-id="element-2916" />
          <SummaryBox label="Warnings" value={summary.warning} color={summary.warning > 0 ? 'amber' : 'emerald'} data-id="element-2917" />
          <SummaryBox label="Info" value={summary.info} color="blue" data-id="element-2918" />
        </div>
      </Card>

      {rows.length === 0 ? <Card className="p-12 text-center" data-id="element-2919">
          <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto mb-3" data-id="element-2920" />
          <h3 className="text-lg font-bold text-slate-900" data-id="element-2921">No visual issues</h3>
          <p className="text-slate-500" data-id="element-2922">
            All questions pass visual governance checks in the current filter
            scope.
          </p>
        </Card> : <div className="space-y-3" data-id="element-2923">
          {rows.slice(0, 100).map(record => {
        const hasCritical = record.issues.some(i => i.severity === 'critical');
        return <Card key={record.question.id} className={`p-4 border-l-4 ${hasCritical ? 'border-l-rose-500' : 'border-l-amber-400'}`} data-id="element-2924">
                <div className="flex items-center justify-between mb-3" data-id="element-2925">
                  <code className="text-sm font-bold text-slate-900 bg-slate-100 px-2 py-0.5 rounded" data-id="element-2926">
                    {record.question.id}
                  </code>
                  <div className="flex gap-1.5" data-id="element-2927">
                    <Badge variant="neutral" size="sm" data-id="element-2928">
                      {record.question.module}
                    </Badge>
                    <Badge variant="neutral" size="sm" data-id="element-2929">
                      T{record.question.tier} · G{record.question.group}
                    </Badge>
                  </div>
                </div>
                <ul className="space-y-1.5" data-id="element-2930">
                  {record.issues.map(issue => <li key={issue.id} className="flex items-start gap-2 text-sm" data-id="element-2931">
                      <SeverityIcon severity={issue.severity} data-id="element-2932" />
                      <span className={issue.severity === 'critical' ? 'text-rose-700' : issue.severity === 'warning' ? 'text-amber-700' : 'text-slate-600'} data-id="element-2933">
                        {issue.message}
                      </span>
                    </li>)}
                </ul>
              </Card>;
      })}
          {rows.length > 100 && <p className="text-center text-sm text-slate-500 py-2" data-id="element-2934">
              Showing 100 of {rows.length} — use filters to narrow results.
            </p>}
        </div>}
    </div>;
}
function SummaryBox({
  label,
  value,
  color
}: {
  label: string;
  value: number;
  color: 'slate' | 'rose' | 'amber' | 'emerald' | 'blue';
}) {
  const colors = {
    slate: 'bg-slate-50 border-slate-200 text-slate-700',
    rose: 'bg-rose-50 border-rose-200 text-rose-700',
    amber: 'bg-amber-50 border-amber-200 text-amber-700',
    emerald: 'bg-emerald-50 border-emerald-200 text-emerald-700',
    blue: 'bg-blue-50 border-blue-200 text-blue-700'
  };
  return <div className={`rounded-xl border p-3 text-center ${colors[color]}`} data-id="element-2935">
      <div className="text-2xl font-black" data-id="element-2936">{value}</div>
      <div className="text-xs font-semibold uppercase tracking-wider opacity-70 mt-0.5" data-id="element-2937">
        {label}
      </div>
    </div>;
}
function SeverityIcon({
  severity
}: {
  severity: string;
}) {
  if (severity === 'critical') return <AlertTriangle className="w-4 h-4 text-rose-500 shrink-0 mt-0.5" data-id="element-2938" />;
  if (severity === 'warning') return <AlertCircle className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" data-id="element-2939" />;
  return <Info className="w-4 h-4 text-blue-400 shrink-0 mt-0.5" data-id="element-2940" />;
}